<template>
  <div class="pagination-container mt8 mb8" v-show="!shouldHide">
    <ul class="pagination">
      <li class="page-item" :class="{disabled: currentPage <= 1}" @click="prevPageClick">
        <a class="page-link">&laquo;</a>
      </li>
      <li
        class="page-item"
        v-for="{ pageNum } in pageItems"
        :key="pageNum"
        :class="{active: pageNum === currentPage}"
        @click="pageClick(pageNum)"
      >
        <a class="page-link">{{pageNum}}</a>
      </li>
      <li class="page-item" :class="{disabled: currentPage >= maxPage}" @click="nextPageClick">
        <a class="page-link">&raquo;</a>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { PageItem } from 'src/models';

// withDefaultsでdefaultを与えても、TS上optional(?)でないとrequired:trueのままになるVueの仕様
// https://github.com/vuejs/core/blob/v3.5.35/packages/compiler-sfc/src/script/resolveType.ts (required: !e.optional)
// currentPage/totalItems/itemsPerPageは全呼び出し元が渡しているため必須のまま。autoHideのみoptionalのまま
interface Props {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  autoHide?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  autoHide: true,
});

const emit = defineEmits(['page-click']);

const maxPage = computed<number>(() => {
  return Math.ceil(props.totalItems / props.itemsPerPage);
});
const shouldHide = computed<boolean>(() => {
  if (!props.autoHide) return false;

  return maxPage.value < 2;
});
const pageItems = computed<PageItem[]>(() => {
  const fromPage = 1;
  const toPage = maxPage.value;
  const ret: PageItem[] = [];
  for (let n = fromPage; n <= toPage; n++) {
    ret.push({ pageNum: n });
  }
  return ret;
});

const pageClick = (pageNum: number) => {
  emit('page-click', { pageNum });
};
const prevPageClick = () => {
  if (props.currentPage <= 1) return;

  pageClick(props.currentPage - 1);
};
const nextPageClick = () => {
  if (props.currentPage >= maxPage.value) return;

  pageClick(props.currentPage + 1);
};
</script>

<style lang="scss" scoped>
.pagination-container {
  display: flex;
  justify-content: center;
}
.pagination {
  margin: 0;
}
</style>
